import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Button
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { auth } from '../../config/firebase';
import { applyActionCode, checkActionCode } from 'firebase/auth';
import { apiClient } from '../../services/api';

const EmailVerifiedPage: React.FC = () => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Verifying your email...');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        // Get the action code from URL parameters
        const oobCode = searchParams.get('oobCode');

        // If no oobCode, check localStorage for pending email
        if (!oobCode) {
          const pendingEmail = localStorage.getItem('pendingVerificationEmail');

          if (pendingEmail) {
            // Firebase already verified, just update backend
            console.log('No oobCode, but found pending email. Updating backend...');

            try {
              await apiClient.post('/auth/verify-email', { email: pendingEmail });

              setStatus('success');
              setMessage('Email verified successfully! You can now log in.');

              localStorage.removeItem('pendingVerificationEmail');

              setTimeout(() => {
                navigate('/login');
              }, 3000);
              return;
            } catch (backendError: any) {
              console.error('Backend update error:', backendError);

              // If email already verified, that's OK
              if (backendError.response?.data?.message?.includes('already verified')) {
                setStatus('success');
                setMessage('Email verified successfully! You can now log in.');
                localStorage.removeItem('pendingVerificationEmail');
                setTimeout(() => navigate('/login'), 3000);
                return;
              }
            }
          }

          setStatus('error');
          setMessage('Verification complete! Please try logging in.');
          return;
        }

        console.log('Found oobCode, checking action code...');

        // First, check the action code to get the email
        const info = await checkActionCode(auth, oobCode);
        const email = info.data.email;

        console.log('Action code valid, email:', email);

        // Apply the verification code to Firebase
        await applyActionCode(auth, oobCode);

        console.log('Email verified in Firebase');

        if (email) {
          console.log('Updating backend for email:', email);

          // Call backend to update emailVerified status
          await apiClient.post('/auth/verify-email', { email });

          console.log('Backend updated successfully');
        }

        setStatus('success');
        setMessage('Email verified successfully! You can now log in.');

        // Clean up localStorage
        localStorage.removeItem('pendingVerificationEmail');

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } catch (error: any) {
        console.error('Verification error:', error);

        let errorMessage = 'Failed to verify email. ';

        if (error.code === 'auth/invalid-action-code') {
          errorMessage = 'The verification link has already been used. Please try logging in.';

          // Try to update backend with localStorage email
          const pendingEmail = localStorage.getItem('pendingVerificationEmail');
          if (pendingEmail) {
            try {
              await apiClient.post('/auth/verify-email', { email: pendingEmail });
              setStatus('success');
              setMessage('Email verified! You can now log in.');
              localStorage.removeItem('pendingVerificationEmail');
              setTimeout(() => navigate('/login'), 3000);
              return;
            } catch (e) {
              console.error('Backend update failed:', e);
            }
          }
        } else if (error.code === 'auth/expired-action-code') {
          errorMessage = 'The verification link has expired. Please request a new one.';
        } else {
          errorMessage += error.message || 'Please try logging in.';
        }

        setStatus('error');
        setMessage(errorMessage);
      }
    };

    verifyEmail();
  }, [navigate, searchParams]);

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
            textAlign: 'center',
            borderRadius: 2
          }}
        >
          {status === 'loading' && (
            <>
              <CircularProgress size={60} sx={{ mb: 3 }} />
              <Typography variant="h5" gutterBottom>
                {message}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please wait while we verify your email...
              </Typography>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircleIcon
                sx={{
                  fontSize: 80,
                  color: 'success.main',
                  mb: 2
                }}
              />
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                Email Verified!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {message}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Redirecting to login page...
              </Typography>
            </>
          )}

          {status === 'error' && (
            <>
              <ErrorIcon
                sx={{
                  fontSize: 80,
                  color: 'error.main',
                  mb: 2
                }}
              />
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                Verification Failed
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {message}
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/login')}
                fullWidth
              >
                Go to Login
              </Button>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default EmailVerifiedPage;
